/**
 * Organization Import Modal
 * Låter användaren ladda upp en Excel-fil och importera underorganisationer
 * under en specifik toppnod.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { BaseModal } from '@/components/common/BaseModal';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import type { Organization } from '@/types/organizations';

interface ParsedRow {
  namn: string;
  foraldraorganisation?: string;
  kontaktperson?: string;
  kontakttelefon?: string;
  epost?: string;
  kostnadsnummer?: string;
}

interface ImportResult {
  created: number;
  errors: Array<{ row: number; name: string; error: string }>;
}

interface Props {
  parentOrg: Organization;
  onClose: () => void;
  onSuccess: () => void;
}

const OrganizationImportModal: React.FC<Props> = ({ parentOrg, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const readPreview = useCallback((selectedFile: File) => {
    // Klientsidig förhandsgranskning via FileReader + XLSX (dynamisk import)
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const XLSX = await import('xlsx');
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });
        setPreviewRows(
          rows.slice(0, 100).map((r) => ({
            namn: (r['Namn'] ?? '').toString().trim(),
            foraldraorganisation: (r['Föräldraorganisation'] ?? '').toString().trim() || undefined,
            kontaktperson: (r['Kontaktperson'] ?? '').toString().trim() || undefined,
            kontakttelefon: (r['Kontakttelefon'] ?? '').toString().trim() || undefined,
            epost: (r['E-post'] ?? '').toString().trim() || undefined,
            kostnadsnummer: (r['Kostnadsnummer'] ?? '').toString().trim() || undefined,
          }))
        );
      } catch {
        toast.error(t('organizations.importFileError'));
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, [t]);

  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setResult(null);
    readPreview(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileChange(dropped);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error(t('organizations.importNoFile'));
      return;
    }
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('orgId', String(parentOrg.id));
      const res = await api.post<{ success: boolean; data: ImportResult }>(
        '/api/organizations/import',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.success) {
        setResult(res.data.data);
      } else {
        toast.error(t('organizations.importError'));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? t('organizations.importError'));
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (result && result.created > 0) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const hasValidRows = previewRows.some((r) => r.namn);

  return (
    <BaseModal
      isOpen={true}
      onClose={handleClose}
      title={t('organizations.importModalTitle')}
      subtitle={`${t('organizations.importModalSubtitle')} "${parentOrg.name}"`}
      size="4xl"
      className="flex flex-col"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-bgDefault border border-borderDefault rounded-lg text-textDefault hover:bg-bgHover transition-colors"
          >
            {result ? t('common.close') : t('common.cancel')}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              disabled={!file || !hasValidRows || importing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {importing && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {t('organizations.importStart')}
              {hasValidRows && !importing && ` (${previewRows.filter((r) => r.namn).length})`}
            </button>
          )}
        </div>
      }
    >
      <div className="flex flex-col min-h-0 gap-5">
        {result ? (
          /* Resultatvy */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg flex items-center gap-3">
                <CheckCircleIcon className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <div className="text-2xl font-bold text-green-700">{result.created}</div>
                  <div className="text-sm text-green-600">{t('organizations.importCreated')}</div>
                </div>
              </div>
              <div className={`border p-4 rounded-lg flex items-center gap-3 ${result.errors.length > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
                {result.errors.length > 0
                  ? <XCircleIcon className="w-8 h-8 text-red-600 flex-shrink-0" />
                  : <CheckCircleIcon className="w-8 h-8 text-textTertiary flex-shrink-0" />}
                <div>
                  <div className={`text-2xl font-bold ${result.errors.length > 0 ? 'text-red-700' : 'text-textTertiary'}`}>
                    {result.errors.length}
                  </div>
                  <div className={`text-sm ${result.errors.length > 0 ? 'text-red-600' : 'text-textTertiary'}`}>
                    {t('organizations.importErrors')}
                  </div>
                </div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-textDefault flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                  {t('organizations.importErrors')}
                </h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-2 rounded text-sm flex gap-2">
                      <span className="text-red-500 font-medium flex-shrink-0">
                        {t('organizations.importRowError')} {e.row}: {e.name}
                      </span>
                      <span className="text-red-600">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Dropzon */}
            <div
              className={`shrink-0 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-borderDefault hover:border-blue-400 hover:bg-bgElevated'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <DocumentArrowUpIcon className="w-10 h-10 text-textTertiary mx-auto mb-3" />
              {file ? (
                <div>
                  <p className="font-medium text-textDefault">{t('organizations.importFileSelected')}</p>
                  <p className="text-blue-600 mt-1">{file.name}</p>
                </div>
              ) : (
                <p className="text-textSecondary text-sm">{t('organizations.importDropzone')}</p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>

            {/* Förhandsgranskning */}
            {previewRows.length > 0 && (
              <div className="min-h-0 flex flex-col">
                <h3 className="font-medium text-textDefault mb-2 shrink-0">
                  {t('organizations.importPreviewTitle')} —{' '}
                  <span className="text-blue-600">{previewRows.filter((r) => r.namn).length}</span>{' '}
                  {t('organizations.importPreviewCount')}
                </h3>
                {previewRows.filter((r) => r.namn).length > 20 && (
                  <p className="text-xs text-textSecondary mb-2 shrink-0">
                    {t('organizations.importPreviewScrollHint')}
                  </p>
                )}
                <div className="overflow-x-auto overflow-y-auto max-h-[calc(2.25rem*21)] rounded-lg border border-borderDefault">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-bgElevated text-textSecondary shadow-sm">
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnName')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnParent')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnContact')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnPhone')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnEmail')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('organizations.importColumnCostNo')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.filter((r) => r.namn).map((row, i) => (
                        <tr
                          key={i}
                          className={`border-t border-borderDefault ${i % 2 === 0 ? 'bg-bgContainer' : 'bg-bgElevated'}`}
                        >
                          <td className="px-3 py-2 font-medium text-textDefault">{row.namn}</td>
                          <td className="px-3 py-2 text-textSecondary">{row.foraldraorganisation ?? '—'}</td>
                          <td className="px-3 py-2 text-textSecondary">{row.kontaktperson ?? '—'}</td>
                          <td className="px-3 py-2 text-textSecondary">{row.kontakttelefon ?? '—'}</td>
                          <td className="px-3 py-2 text-textSecondary">{row.epost ?? '—'}</td>
                          <td className="px-3 py-2 text-textSecondary">{row.kostnadsnummer ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default OrganizationImportModal;
